import { useEffect } from 'react';

/**
 * SEO Head component for managing page meta tags
 * Updates document title and meta description for better SEO
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords = [],
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false 
}) => {
  useEffect(() => {
    // Update document title
    const fullTitle = title ? `${title} | Mirevy - Luxury Botanical Skincare` : 'Mirevy - Luxury Botanical Skincare | Made in UK';
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update meta description
    if (description) {
      updateMetaTag('description', description);
    }

    // Update keywords
    if (keywords.length > 0) {
      updateMetaTag('keywords', keywords.join(', '));
    }

    // Update robots
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    if (description) {
      updateMetaTag('og:description', description, true);
    }
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:site_name', 'Mirevy', true);
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }
    
    if (canonicalUrl) {
      updateMetaTag('og:url', canonicalUrl, true);
      
      // Update or create canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    if (description) {
      updateMetaTag('twitter:description', description);
    }
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Cleanup function to reset title on unmount
    return () => {
      // Optionally reset to default title
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex]);

  return null; // This component doesn't render anything
};

/**
 * Generate structured data for a collection page
 */
export const generateCollectionSchema = (collection, products = []) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": collection.name,
    "description": collection.longDescription || collection.description,
    "url": `https://mirevy.com/collections/${collection.slug}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 10).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.image,
          "brand": {
            "@type": "Brand",
            "name": "Mirevy"
          },
          "offers": {
            "@type": "Offer",
            "price": product.price,
            "priceCurrency": "GBP",
            "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
          }
        }
      }))
    }
  };

  return schema;
};

/**
 * Generate structured data for a product page
 */
export const generateProductSchema = (product, reviews = []) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images?.length > 0 ? product.images : [product.image],
    "brand": {
      "@type": "Brand",
      "name": "Mirevy"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Mirevy",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "London",
        "addressRegion": "Edgware",
        "addressCountry": "UK"
      }
    },
    "offers": {
      "@type": "Offer",
      "url": `https://mirevy.com/product/${product.id}`,
      "priceCurrency": "GBP",
      "price": product.price,
      "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Mirevy"
      }
    }
  };

  // Add aggregate rating if available
  if (product.rating > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.rating,
      "reviewCount": product.review_count || reviews.length || 1,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  // Add reviews if available
  if (reviews.length > 0) {
    schema.review = reviews.slice(0, 5).map(review => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": 5
      },
      "author": {
        "@type": "Person",
        "name": review.user_name || "Verified Buyer"
      },
      "reviewBody": review.comment
    }));
  }

  return schema;
};

/**
 * Generate structured data for the organization
 */
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Mirevy",
  "description": "Luxury botanical skincare made in the UK with natural ingredients for sensitive skin.",
  "url": "https://mirevy.com",
  "logo": "https://mirevy.com/logo-darker.png",
  "sameAs": [],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "London",
    "addressRegion": "Edgware",
    "addressCountry": "UK"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44-7388-071719",
    "contactType": "customer service",
    "email": "Mmaary.2007@gmail.com"
  }
});

/**
 * JSON-LD Script component for structured data
 */
export const JsonLd = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

export default SEOHead;
